import React, { useState } from 'react';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  IconButton,
  Tooltip,
  Box,
  CardActions,
  Chip,
  useTheme,
  alpha,
  Button
} from '@mui/material';
import {
  Delete as DeleteIcon,
  PictureAsPdf as PdfIcon,
  Image as ImageIcon,
  InsertDriveFile as FileIcon,
  Visibility as VisibilityIcon,
  Download as DownloadIcon,
  Architecture as CadIcon
} from '@mui/icons-material';
import { Documento, Obra } from '../../types';
import DwgViewer from './DwgViewer';

interface DocumentosGaleriaProps {
  documentos: Documento[];
  obras: Obra[];
  onBorrarDocumento: (documento: Documento) => void;
  loading: boolean;
}

// Check if file is a DWG/DXF file
const isDwgFile = (mimeType: string, nombre: string): boolean => {
  const extension = nombre?.toLowerCase().split('.').pop();
  return extension === 'dwg' || extension === 'dxf' ||
    mimeType?.includes('dwg') || mimeType?.includes('dxf') ||
    mimeType?.includes('autocad') || mimeType?.includes('acad');
};

const DocumentosGaleria: React.FC<DocumentosGaleriaProps> = ({
  documentos,
  obras,
  onBorrarDocumento,
  loading
}) => {
  const theme = useTheme();
  const [dwgViewerOpen, setDwgViewerOpen] = useState(false);
  const [selectedDwg, setSelectedDwg] = useState<Documento | null>(null);

  const getIconForType = (mimeType: string, nombre: string) => {
    if (isDwgFile(mimeType, nombre)) {
      return <CadIcon sx={{ fontSize: 60, color: '#00bcd4', opacity: 0.9 }} />;
    }
    if (mimeType?.includes('pdf')) return <PdfIcon sx={{ fontSize: 60, color: 'error.main', opacity: 0.8 }} />;
    if (mimeType?.includes('image')) return <ImageIcon sx={{ fontSize: 60, color: 'primary.main', opacity: 0.8 }} />;
    return <FileIcon sx={{ fontSize: 60, color: 'text.secondary', opacity: 0.5 }} />;
  };

  const getObraNombre = (obraId: string) => {
    if (!obraId) return 'Sin obra asignada';

    const cleanObraId = String(obraId).trim();
    const obra = obras.find(o => o.id && String(o.id).trim() === cleanObraId);

    return obra ? obra.nombre : `Obra desconocida (ID: "${obraId}")`;
  };

  const formatDate = (date: any) => {
    if (!date) return '';
    // Handle Firestore Timestamp or Date object
    const d = date.toDate ? date.toDate() : new Date(date);
    return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const handleViewDocument = (doc: Documento) => {
    if (isDwgFile(doc.mimeType, doc.nombre)) {
      setSelectedDwg(doc);
      setDwgViewerOpen(true);
    } else {
      // Open in new tab for other file types
      window.open(doc.url, '_blank', 'noopener,noreferrer');
    }
  };

  const handleCloseDwgViewer = () => {
    setDwgViewerOpen(false);
    setSelectedDwg(null);
  };

  return (
    <>
      <Grid container spacing={3}>
        {documentos.map((doc) => (
          <Grid item xs={12} sm={6} md={4} lg={3} key={doc.id}>
            <Card
              elevation={0}
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                borderRadius: 3,
                border: '1px solid',
                borderColor: isDwgFile(doc.mimeType, doc.nombre) ? 'info.light' : 'divider',
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: theme.shadows[4],
                  borderColor: isDwgFile(doc.mimeType, doc.nombre) ? 'info.main' : 'primary.light'
                }
              }}
            >
              <Box
                sx={{
                  position: 'relative',
                  height: 180,
                  bgcolor: isDwgFile(doc.mimeType, doc.nombre) ? 'rgba(0, 188, 212, 0.05)' : 'grey.50',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  overflow: 'hidden',
                  borderBottom: '1px solid',
                  borderColor: 'divider'
                }}
              >
                {doc.thumbnailUrl ? (
                  <Box
                    component="img"
                    src={doc.thumbnailUrl}
                    alt={doc.nombre}
                    sx={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      transition: 'transform 0.3s ease',
                      '&:hover': {
                        transform: 'scale(1.05)'
                      }
                    }}
                  />
                ) : (
                  <Box sx={{
                    p: 3,
                    borderRadius: '50%',
                    bgcolor: 'background.paper',
                    boxShadow: 1
                  }}>
                    {getIconForType(doc.mimeType || '', doc.nombre)}
                  </Box>
                )}

                {isDwgFile(doc.mimeType, doc.nombre) ? (
                  <Chip
                    label="CAD"
                    size="small"
                    sx={{
                      position: 'absolute',
                      top: 12,
                      right: 12,
                      fontWeight: 600,
                      boxShadow: 1,
                      bgcolor: '#00bcd4',
                      color: 'white'
                    }}
                  />
                ) : doc.tipo && (
                  <Chip
                    label={doc.tipo}
                    size="small"
                    color="primary"
                    sx={{
                      position: 'absolute',
                      top: 12,
                      right: 12,
                      fontWeight: 600,
                      boxShadow: 1
                    }}
                  />
                )}
              </Box>

              <CardContent sx={{ flexGrow: 1, p: 2.5 }}>
                <Typography
                  variant="subtitle1"
                  component="div"
                  noWrap
                  title={doc.nombre}
                  sx={{ fontWeight: 600, mb: 0.5 }}
                >
                  {doc.nombre}
                </Typography>

                <Typography
                  variant="body2"
                  color="primary"
                  sx={{ mb: 1.5, fontWeight: 500, fontSize: '0.875rem' }}
                >
                  {getObraNombre(doc.obraId)}
                </Typography>

                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    minHeight: '2.5em',
                    mb: 2,
                    fontSize: '0.85rem'
                  }}
                >
                  {doc.descripcion || 'Sin descripción'}
                </Typography>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="caption" sx={{ color: 'text.disabled', bgcolor: 'action.hover', px: 1, py: 0.5, borderRadius: 1 }}>
                    {formatDate(doc.fechaSubida)}
                  </Typography>
                </Box>
              </CardContent>

              <CardActions sx={{ p: 2, pt: 0, justifyContent: 'space-between' }}>
                <Button
                  size="small"
                  startIcon={<VisibilityIcon />}
                  onClick={() => handleViewDocument(doc)}
                  sx={{
                    textTransform: 'none',
                    borderRadius: 2,
                    color: isDwgFile(doc.mimeType, doc.nombre) ? '#00bcd4' : undefined
                  }}
                >
                  Ver
                </Button>

                <Tooltip title="Eliminar documento">
                  <IconButton
                    size="small"
                    onClick={() => onBorrarDocumento(doc)}
                    disabled={loading}
                    sx={{
                      color: 'text.secondary',
                      '&:hover': { color: 'error.main', bgcolor: alpha(theme.palette.error.main, 0.1) }
                    }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* DWG Viewer Modal */}
      {selectedDwg && (
        <DwgViewer
          open={dwgViewerOpen}
          onClose={handleCloseDwgViewer}
          fileUrl={selectedDwg.url}
          fileName={selectedDwg.nombre}
        />
      )}
    </>
  );
};

export default DocumentosGaleria;

